import React, { useState, useCallback, useEffect } from 'react'
import { Button, Typography, Input, List, Card, Spin, message } from 'antd'
import { ArrowLeftOutlined, SearchOutlined, ShopOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { dealerAPI } from '../services/api'
import './DealerSelectionPage.css'

const { Title, Text } = Typography
const { Search } = Input

interface Dealer {
  id: number | string
  name: string
  dealer_name: string
  address?: string
  wecom_dept_id?: string
  sj_code?: string
  coordinates?: string
  business_status?: string
  phone?: string
  contact_person?: string
}

const DealerSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [dealers, setDealers] = useState<Dealer[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [searching, setSearching] = useState(false)

  // 进入页面时加载第一页经销商（关键词为空时后端返回全部）
  useEffect(() => {
    handleSearch('', 1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBack = () => {
    // 检查是否有返回路径，如果没有则默认返回试乘试驾邀请页面
    const state = location.state as any
    const returnTo = state?.returnTo || '/test-drive-invitation'
    
    // 如果是强制选择模式，不允许返回
    if (state?.forceSelection) {
      message.warning('请先选择经销商才能继续使用')
      return
    }
    
    navigate(returnTo, { 
      state: state?.selectedDealer ? { selectedDealer: state.selectedDealer } : {} 
    })
  }

  const handleSearch = useCallback(async (keyword: string, page: number = 1, append: boolean = false) => {
    try {
      if (page === 1) {
        setSearching(true)
        setDealers([])
        setCurrentPage(1)
        setHasMore(true)
      }
      
      setLoading(true)
      const response = await dealerAPI.searchDealers({
        keyword: keyword.trim(),
        page,
        limit: 10
      }) as any
      // 兼容：后端 data 可能在 response.data 或 response.data.data
      const data = response.data ?? response
      const newDealers = (data.dealers ?? []) as Dealer[]

      if (response.success) {
        if (append) {
          setDealers(prev => [...prev, ...newDealers])
        } else {
          setDealers(newDealers)
        }
        
        setHasMore(newDealers.length === 10)
        setCurrentPage(page)
        
        if (page === 1 && newDealers.length === 0) {
          message.info('未找到相关经销商')
        }
      } else {
        message.error(response.message || '搜索失败')
      }
    } catch (error: any) {
      console.error('搜索经销商失败:', error)
      message.error('搜索失败，请重试')
    } finally {
      setLoading(false)
      setSearching(false)
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value)
  }

  const handleSearchSubmit = (value: string) => {
    setSearchKeyword(value)
    handleSearch(value, 1, false)
  }

  const handleLoadMore = () => {
    if (!loading && hasMore && searchKeyword) {
      handleSearch(searchKeyword, currentPage + 1, true)
    }
  }

  const handleDealerSelect = async (dealer: Dealer) => {
    try {
      // 更新销售顾问的所属经销商
      const response = await dealerAPI.updateConsultantDealer(dealer.id) as any
      if (response.success) {
        // 返回到对应的页面
        const state = location.state as any
        const returnTo = state?.returnTo || '/test-drive-invitation'
        console.log('🔄 准备跳转回:', returnTo, '状态:', state)
        navigate(returnTo, {
          state: { 
            selectedDealer: dealer,
            dealerUpdated: true // 标记经销商已更新
          }
        })
        console.log('✅ 跳转命令已执行')
      } else {
        message.error(response.message || '选择失败')
      }
    } catch (error: any) {
      console.error('选择经销商失败:', error)
      message.error('选择失败，请重试')
    }
  }

  return (
    <div className="dealer-selection-page">
      {/* 顶部导航 */}
      <div className="page-header">
        <div className="header-content">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-btn"
          >
            返回
          </Button>
          <Title level={4} className="page-title">
            选择经销商
          </Title>
          <div className="header-spacer"></div>
        </div>
      </div>

      {/* 搜索区域 */}
      <div className="search-section">
        <Search
          placeholder="请输入经销商名称或地址"
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          value={searchKeyword}
          onChange={handleSearchChange}
          onSearch={handleSearchSubmit}
          loading={searching}
          className="search-input"
        />
      </div>

      {/* 页面内容 */}
      <div className="page-content">
        {loading && dealers.length === 0 ? (
          <div className="loading-container">
            <Spin size="large" />
            <Text className="loading-text">搜索中...</Text>
          </div>
        ) : dealers.length > 0 ? (
          <div className="dealers-list">
            <List
              dataSource={dealers}
              renderItem={(dealer) => (
                <List.Item key={dealer.id} className="dealer-item">
                  <Card
                    className="dealer-card"
                    onClick={() => handleDealerSelect(dealer)}
                  >
                    <div className="dealer-info">
                      <div className="dealer-name">
                        <ShopOutlined className="dealer-icon" />
                        {dealer.name}
                      </div>
                      <div className="dealer-address">{dealer.address ?? '-'}</div>
                      {dealer.sj_code && (
                        <div className="dealer-sj-code">SJ代码: {dealer.sj_code}</div>
                      )}
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            
            {/* 加载更多按钮 */}
            {hasMore && (
              <div className="load-more-container">
                <Button
                  type="default"
                  size="large"
                  onClick={handleLoadMore}
                  loading={loading}
                  className="load-more-btn"
                >
                  {loading ? '加载中...' : '加载更多'}
                </Button>
              </div>
            )}
            
            {/* 没有更多结果提示 */}
            {!hasMore && dealers.length > 0 && (
              <div className="no-more-container">
                <Text type="secondary">没有更多搜索结果</Text>
              </div>
            )}
          </div>
        ) : searchKeyword ? (
          <div className="empty-container">
            <Text type="secondary">未找到相关经销商</Text>
          </div>
        ) : (
          <div className="placeholder-container">
            <Text type="secondary">请输入关键词搜索经销商</Text>
          </div>
        )}
      </div>
    </div>
  )
}

export default DealerSelectionPage
